import React, { useCallback, useEffect, useState } from 'react';
import { BodyText, BoldBodyText, ISelectProps, Select } from 'ui-toolkit/index';
import { useSelector } from 'react-redux';
import { IOrderOption } from '../../../types/accounting/IOrderOption';
import { getOrdersSearchAction, setOrdersSearchAction } from '../../../store/orders';
import { RootStore } from '../../../store/types';
import useDebounce from '../../../utilities/hooks/useDebounce';
import { addressDataToString } from '../../../ui-toolkit/orders/interfaces/IAddressResponse';
import {
  createDefaultSearchOrderResultData,
  ISearchOrderResultData,
} from '../../../ui-toolkit/orders/searchOrder/types/ISearchOrdersResult';
import { getBorrowerStringFromParties } from '../../../ui-toolkit/orders/searchOrder/types/ISearchOrder';
import { eSearchOrdersOrderBy } from '../../../ui-toolkit/orders/searchOrder/types/eSearchOrdersOrderBy';
import { useAppDispatch } from '../../../store/store';

const SEARCH_DELAY = 350;

const generateOrderOptions = (searchResult: ISearchOrderResultData) =>
  searchResult.content.map((value) => ({
    data: value,
    label: (
      <div>
        <BoldBodyText>{value.fileNumber}</BoldBodyText>
        <BodyText>{addressDataToString(value.propertyAddress)}</BodyText>
        <BodyText>{getBorrowerStringFromParties(value.parties)}</BodyText>
      </div>
    ),
    selectedLabel: <BodyText>{value.fileNumber}</BodyText>,
    value: value.id,
  }));

interface IOrderSelectProps extends ISelectProps {
  onChange: (orderId: string) => void;
  onOptionSet: (option?: IOrderOption) => void;
  value: string;
}

export const OrderSelect: React.FC<IOrderSelectProps> = ({ onOptionSet, ...props }) => {
  const searchResult = useSelector((state: RootStore) => state.ordersSearch.data);
  const searchResultFetchStatus = useSelector((state: RootStore) => state.ordersSearch.fetchStatus);
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, SEARCH_DELAY);
  const [orderOptions, setOrderOptions] = useState<IOrderOption[]>(generateOrderOptions(searchResult));

  const getSelectedOption = useCallback(
    (value?: string) => {
      return value ? orderOptions.find((option) => option.value === value) : undefined;
    },
    [orderOptions]
  );

  const [selectedOption, setSelectedOption] = useState(getSelectedOption(props.value));

  useEffect(() => {
    if (searchResultFetchStatus.fetched && searchResultFetchStatus.success) {
      setOrderOptions(generateOrderOptions(searchResult));
    }
  }, [searchResult, searchResultFetchStatus]);

  useEffect(() => {
    if (orderOptions) setSelectedOption(getSelectedOption(props.value));
  }, [orderOptions, props.value, setSelectedOption, getSelectedOption]);

  useEffect(() => {
    if (onOptionSet) onOptionSet(selectedOption);
  }, [selectedOption, onOptionSet]);

  useEffect(() => {
    if (debouncedSearchText)
      dispatch(
        getOrdersSearchAction({
          fileNumbers: [debouncedSearchText],
          sortBy: eSearchOrdersOrderBy.createdAtDESC,
          pageNumber: 0,
          pageSize: 5,
        })
      );
    else dispatch(setOrdersSearchAction(createDefaultSearchOrderResultData()));
  }, [debouncedSearchText, dispatch]);

  return (
    <Select
      {...props}
      formatOptionLabel={(option: any, { context }: any) => (context === 'menu' ? option.label : option.selectedLabel)}
      filterOption={
        () => true /*skipping ReactSelect filter since it's managed from the above useEffect & debouncedSearchText*/
      }
      loadingMessage={(obj: any) => `Searching for '${obj.inputValue}'`}
      menuPlacement="auto"
      menuPosition="fixed"
      onChange={(value: any) => {
        if (props.onChange) props.onChange(value ? value.value : undefined);
      }}
      onInputChange={(newValue: any, actionMeta: any) => {
        if (actionMeta.action === 'input-change') setSearchText(newValue);
      }}
      onMenuOpen={() => undefined}
      onMenuClose={() => undefined}
      options={orderOptions}
      isLoading={searchResultFetchStatus.fetching}
      styles={{
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        clearIndicator: (base: any) => ({ ...base, padding: '6px' }),
      }}
      value={selectedOption}
    />
  );
};
